import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Network } from 'vis-network/standalone';
import { DataSet } from 'vis-data/standalone';
import NodeEditModal from './NodeEditModal';

interface StoryNode {
  id: string;
  label: string;
  title?: string;
  color?: string;
  shape?: string;
}

interface StoryEdge {
  id?: string;
  from: string;
  to: string;
  arrows?: string;
  color?: string;
  label?: string;
  type?: string;
}

interface StoryTreeProps {
  nodes: StoryNode[];
  edges: StoryEdge[];
  onNodeClick?: (nodeId: string, nodePosition?: { x: number; y: number }) => void;
  onNodeDoubleClick?: (nodeId: string) => void;
  onNodeEdit?: (nodeId: string) => void;
  onNodeDelete?: (nodeId: string) => void;
  onAddChildNode?: (parentId: string, nodeData: { label: string; title: string; description: string; content: string }) => void; // 修改
  selectedNodeId?: string | null;
  storyPath?: string[]; // 故事路径用于高亮显示
  editingNodeId?: string;
  isLoading?: boolean;
}

const StoryTree: React.FC<StoryTreeProps> = ({ 
  nodes, 
  edges, 
  onNodeClick, 
  onNodeDoubleClick, 
  onNodeEdit, 
  onNodeDelete, 
  onAddChildNode,
  selectedNodeId, 
  storyPath = [],
  editingNodeId, 
  isLoading = false 
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const networkRef = useRef<Network | null>(null);
  const nodesDataSetRef = useRef<DataSet<any> | null>(null);
  const edgesDataSetRef = useRef<DataSet<any> | null>(null);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{ visible: boolean; x: number; y: number; nodeId?: string }>({ visible: false, x: 0, y: 0 });
  const [showAddChildModal, setShowAddChildModal] = useState(false);
  const [addChildParentId, setAddChildParentId] = useState<string | null>(null);

  // Memoize processed nodes to avoid unnecessary recalculations
  const processedNodes = useMemo(() => {
    // 找到根节点（没有入边的节点）
    const nodeIds = new Set(nodes.map(n => n.id));
    const hasIncomingEdge = new Set(edges.map(e => e.to));
    const rootNodes = Array.from(nodeIds).filter(id => !hasIncomingEdge.has(id));
    
    return nodes.map(node => {
      const isInPath = storyPath.includes(node.id);
      const isSelected = selectedNodeId === node.id;
      const isEditing = editingNodeId === node.id;
      const isHovered = hoveredNodeId === node.id;
      const isRoot = rootNodes.includes(node.id);  // 检查是否为根节点
      
      return {
        ...node,
        title: undefined, // 移除 tooltip
        color: {
          background: isSelected ? '#ff9900' : 
                     isEditing ? '#ff6b35' :
                     isRoot ? '#ff0000' :  // 根节点使用红色
                     isInPath ? '#4cc9f0' :  // 路径中的节点使用特殊颜色
                     isHovered ? '#5a73f5' : '#4361ee',
          border: isSelected ? '#ffbe55' : 
                 isEditing ? '#ffbe0b' : 
                 isRoot ? '#ffffff' :  // 根节点使用白色边框
                 isInPath ? '#ffffff' :  // 路径中的节点使用白色边框
                 '#4cc9f0',
          highlight: {
            background: '#ff9900',
            border: '#ffbe55'
          },
          hover: {
            background: '#ffb366',
            border: '#ffbe55'
          }
        },
        borderWidth: isSelected ? 4 : isRoot ? 4 : isInPath ? 3 : 2,  // 根节点边框更粗
        shadow: {
          enabled: true,
          color: isSelected ? 'rgba(255, 153, 0, 0.6)' : 
                 isEditing ? 'rgba(255, 107, 53, 0.6)' : 
                 isRoot ? 'rgba(255, 0, 0, 0.8)' :  // 根节点红色阴影
                 isInPath ? 'rgba(76, 201, 240, 0.8)' :  // 路径中的节点阴影更明显
                 'rgba(67, 97, 238, 0.4)',
          size: isSelected ? 12 : isRoot ? 12 : isInPath ? 10 : 8,
          x: 0,
          y: 0
        }
      };
    });
  }, [nodes, selectedNodeId, editingNodeId, hoveredNodeId, storyPath, edges]);

  // Memoize processed edges to avoid unnecessary recalculations
  const processedEdges = useMemo(() => {
    return edges.map((edge, index) => {
      // 检查这条边是否在当前路径中
      const isInPath = storyPath.length > 1 && 
        storyPath.some((nodeId, idx) => 
          idx < storyPath.length - 1 && 
          edge.from === nodeId && 
          edge.to === storyPath[idx + 1]
        );
      
      return {
        ...edge,
        id: edge.id || `edge-${index}`,
        label: undefined, // 明确移除标签
        smooth: {
          enabled: true,
          type: 'continuous',
          roundness: 0.5
        },
        color: {
          color: isInPath ? '#ffffff' : '#4cc9f0',  // 路径中的边使用白色
          highlight: '#7209b7',
          hover: '#5a73f5'
        },
        width: isInPath ? 4 : 3,  // 路径中的边更粗
        shadow: {
          enabled: true,
          color: isInPath ? 'rgba(255, 255, 255, 0.6)' : 'rgba(76, 201, 240, 0.3)',
          size: isInPath ? 8 : 5,
          x: 0,
          y: 0
        }
      };
    });
  }, [edges, storyPath]);

  // Close context menu
  const closeContextMenu = useCallback(() => {
    console.log(t('storyTree.closeContextMenu'));
    setContextMenu({ visible: false, x: 0, y: 0 });
  }, [t]);

  // Handle node editing
  const handleEditNode = useCallback((nodeId: string) => {
    console.log(t('storyTree.editNodeCalled'), nodeId, t('storyTree.onNodeEditExists'), !!onNodeEdit);
    try {
      onNodeEdit?.(nodeId);
      closeContextMenu();
    } catch (error) {
      console.error(t('storyTree.editNodeError'), error);
    }
  }, [onNodeEdit, closeContextMenu, t]);

  // Handle node deletion
  const handleDeleteNode = useCallback((nodeId: string) => {
    console.log(t('storyTree.deleteNodeCalled'), nodeId, t('storyTree.onNodeDeleteExists'), !!onNodeDelete);
    try {
      onNodeDelete?.(nodeId);
      closeContextMenu();
    } catch (error) {
      console.error(t('storyTree.deleteNodeError'), error);
    }
  }, [onNodeDelete, closeContextMenu, t]);
  // Initialize or update the network
  useEffect(() => {
    if (!containerRef.current) return;

    // Initialize DataSets if they don't exist
    if (!nodesDataSetRef.current) {
      nodesDataSetRef.current = new DataSet(processedNodes);
    }
    if (!edgesDataSetRef.current) {
      edgesDataSetRef.current = new DataSet(processedEdges);
    }
    
    // Create network if it doesn't exist
    if (!networkRef.current) {
      const data = {
        nodes: nodesDataSetRef.current,
        edges: edgesDataSetRef.current,
      };
      
      const options = {
        nodes: {
          shape: 'dot',
          size: 40,
          borderWidth: 3,
          borderWidthSelected: 6,
          font: {
            size: 18,
            face: 'Segoe UI',
            color: '#ffffff',
            strokeWidth: 3,
            strokeColor: 'rgba(0, 0, 0, 0.6)',
            bold: true
          },
          shadow: {
            enabled: true,
            color: 'rgba(67, 97, 238, 0.4)',
            size: 10,
            x: 0,
            y: 0
          }
        },
        edges: {
          width: 4,
          selectionWidth: 6,
          shadow: {
            enabled: true,
            color: 'rgba(76, 201, 240, 0.4)',
            size: 8,
            x: 0,
            y: 0
          },
          arrows: {
            to: {
              enabled: true,
              scaleFactor: 1.4,
              type: 'arrow'
            }
          },
          smooth: {
            enabled: true,
            type: 'continuous',
            roundness: 0.6
          }
        },
        physics: {
          stabilization: {
            enabled: true,
            iterations: 100,
            updateInterval: 25
          },
          barnesHut: {
            gravitationalConstant: -8000,
            springConstant: 0.04,
            springLength: 180,
            damping: 0.09
          }
        },
        layout: {
          hierarchical: {
            direction: 'LR',
            sortMethod: 'hubsize',
            levelSeparation: 200,
            nodeSpacing: 50,
            treeSpacing: 150,
            blockShifting: true,
            edgeMinimization: true,
            parentCentralization: true
          }
        },
        interaction: {
          hover: true,
          tooltipDelay: 100,
          navigationButtons: true,
          keyboard: true,
          zoomView: true,
          dragView: true,
          selectConnectedEdges: false,
          multiselect: false
        }
      };
      
      networkRef.current = new Network(containerRef.current, data, options as any);
    }
    
    return () => {
      if (networkRef.current) {
        networkRef.current.destroy();
        networkRef.current = null;
      }
      nodesDataSetRef.current = null;
      edgesDataSetRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update event handlers when callbacks change
  useEffect(() => {
    if (!networkRef.current) return;

    // Remove all existing listeners
    networkRef.current.off('click');
    networkRef.current.off('doubleClick');
    networkRef.current.off('hoverNode');
    networkRef.current.off('blurNode');
    networkRef.current.off('oncontext');

    // Re-add event handlers with current callbacks
    networkRef.current.on('click', (event) => {
      if (event.nodes.length > 0) {
        const nodeId = event.nodes[0];
        
        // 获取节点在容器中的位置
        let nodePosition: { x: number; y: number } | undefined;
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          // 使用DOM坐标，相对于容器
          nodePosition = {
            x: event.pointer.DOM.x - rect.left,
            y: event.pointer.DOM.y - rect.top
          };
        }
        
        onNodeClick?.(nodeId, nodePosition);
      }
      closeContextMenu();
    });
    
    networkRef.current.on('doubleClick', (event) => {
      if (event.nodes.length > 0 && onNodeDoubleClick) {
        const nodeId = event.nodes[0];
        onNodeDoubleClick(nodeId);
      }
    });
    
    networkRef.current.on('hoverNode', (event) => {
      setHoveredNodeId(event.node);
    });
    
    networkRef.current.on('blurNode', () => {
      setHoveredNodeId(null);
    });
    
    networkRef.current.on('oncontext', (event) => {
      event.event.preventDefault(); // 阻止浏览器默认右键菜单
      
      if (event.nodes.length > 0) {
        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) {
          // 使用DOM坐标，相对于容器
          const x = Math.max(0, Math.min(event.pointer.DOM.x - rect.left, rect.width - 150)); // 150是菜单宽度
          const y = Math.max(0, Math.min(event.pointer.DOM.y - rect.top, rect.height - 100)); // 100是菜单高度
          
          setContextMenu({
            visible: true,
            x,
            y,
            nodeId: event.nodes[0]
          });
        }
      }
    });
  }, [onNodeClick, onNodeDoubleClick, closeContextMenu]);
  
  // Update nodes data reactively
  useEffect(() => {
    if (nodesDataSetRef.current) {
      const currentIds = nodesDataSetRef.current.getIds().map(id => String(id));
      const newIds = processedNodes.map(n => n.id);
      
      // Remove nodes that no longer exist
      const toRemove = currentIds.filter(id => !newIds.includes(id));
      if (toRemove.length > 0) {
        nodesDataSetRef.current.remove(toRemove);
      }
      
      // Update or add nodes
      processedNodes.forEach(node => {
        if (currentIds.includes(node.id)) {
          nodesDataSetRef.current!.update(node);
        } else {
          nodesDataSetRef.current!.add(node);
        }
      });
    }
  }, [processedNodes]);
  
  // Update edges data reactively
  useEffect(() => {
    if (edgesDataSetRef.current) {
      const currentIds = edgesDataSetRef.current.getIds().map(id => String(id));
      const newIds = processedEdges.map(e => e.id!);
      
      // Remove edges that no longer exist
      const toRemove = currentIds.filter(id => !newIds.includes(id));
      if (toRemove.length > 0) {
        edgesDataSetRef.current.remove(toRemove);
      }
      
      // Update or add edges
      processedEdges.forEach(edge => {
        if (currentIds.includes(edge.id!)) {
          edgesDataSetRef.current!.update(edge);
        } else {
          edgesDataSetRef.current!.add(edge);
        }
      });
    }
  }, [processedEdges]);
  
  return (
    <>
      {/* 新建子节点弹窗复用NodeEditModal，始终渲染在最外层，保证遮罩覆盖 */}
      <NodeEditModal
        node={null}
        isVisible={showAddChildModal}
        title={t('nodeModal.create.title')}
        isLoading={false}
        onCancel={() => {
          setShowAddChildModal(false);
          setAddChildParentId(null);
        }}
        onSave={(_id, data) => {
          if (addChildParentId && data.label && data.title && data.content) {
            onAddChildNode?.(addChildParentId, {
              label: data.label,
              title: data.title,
              description: data.title, // 兼容旧字段
              content: data.content
            });
            setShowAddChildModal(false);
            setAddChildParentId(null);
          }
        }}
      />
      <div 
        ref={containerRef} 
        className="visualization"
        style={{
          height: '100%',
          width: '100%',
          background: isLoading 
            ? 'linear-gradient(135deg, rgba(26, 42, 108, 0.2) 0%, rgba(178, 31, 31, 0.1) 50%, rgba(58, 97, 134, 0.2) 100%)'
            : 'linear-gradient(135deg, rgba(26, 42, 108, 0.3) 0%, rgba(178, 31, 31, 0.2) 50%, rgba(58, 97, 134, 0.3) 100%)',
          borderRadius: '16px',
          overflow: 'hidden',
          border: '1px solid rgba(76, 201, 240, 0.2)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          position: 'relative',
          cursor: isLoading ? 'wait' : 'default',
          transition: 'all 0.3s ease'
        }}
        onContextMenu={(e) => {
          e.preventDefault(); // 阻止默认右键菜单
          console.log(t('storyTree.containerRightClickPrevented'));
        }}
        onClick={closeContextMenu}
      >
        {isLoading && (
          <div className="loading-overlay" style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(2px)'
          }}>
            <div style={{
              color: '#4cc9f0',
              fontSize: '18px',
              fontWeight: 'bold',
              textAlign: 'center'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '4px solid rgba(76, 201, 240, 0.3)',
                borderTop: '4px solid #4cc9f0',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 10px'
              }} />
              t('storyTree.generating')
            </div>
          </div>
        )}
        
        {contextMenu.visible && (
          <div 
            className="context-menu"
            style={{
              position: 'absolute',
              top: contextMenu.y,
              left: contextMenu.x,
              background: 'rgba(26, 42, 108, 0.95)',
              border: '1px solid rgba(76, 201, 240, 0.3)',
              borderRadius: '8px',
              padding: '8px 0',
              minWidth: '140px',
              zIndex: 2000,
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
              pointerEvents: 'auto' // 确保菜单可以接收鼠标事件
            }}
            onClick={(e) => {
              console.log(t('storyTree.menuContainerClicked'));
              e.stopPropagation();
            }}
          >
            <div style={{
              padding: '4px 12px',
              fontSize: '12px',
              color: 'rgba(255, 255, 255, 0.6)',
              borderBottom: '1px solid rgba(76, 201, 240, 0.2)',
              marginBottom: '4px'
            }}>
              {/* 显示节点名称（label）而不是id */}
              {t('storyTree.node')}: {(() => {
                const node = nodes.find(n => n.id === contextMenu.nodeId);
                return node ? node.label : contextMenu.nodeId;
              })()}
            </div>
            {onNodeEdit && (
              <button
                className="context-menu-item"
                style={{
                  width: '100%',
                  padding: '8px 16px',
                  background: 'none',
                  border: 'none',
                  color: '#ffffff',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'background 0.2s',
                  display: 'block'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(76, 201, 240, 0.2)';
                  console.log(t('storyTree.mouseEnterEditButton'));
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'none';
                  console.log(t('storyTree.mouseLeaveEditButton'));
                }}
                onClick={(e) => {
                  console.log(t('storyTree.editButtonClicked'));
                  e.preventDefault();
                  e.stopPropagation();
                  if (contextMenu.nodeId) {
                    console.log(t('storyTree.editNodeClicked'), contextMenu.nodeId);
                    handleEditNode(contextMenu.nodeId);
                  }
                }}
              >
                ✏️ {t('storyTree.editNode')}
              </button>
            )}
            {onAddChildNode && (
              <button
                className="context-menu-item"
                style={{
                  width: '100%',
                  padding: '8px 16px',
                  background: 'none',
                  border: 'none',
                  color: '#4cc9f0',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'background 0.2s',
                  display: 'block'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(76, 201, 240, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'none';
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setAddChildParentId(contextMenu.nodeId || null);
                  setShowAddChildModal(true);
                  closeContextMenu();
                }}
              >
                ➕ {t('storyTree.addManualNode')}
              </button>
            )}
            {onNodeDelete && (
              <button
                className="context-menu-item"
                style={{
                  width: '100%',
                  padding: '8px 16px',
                  background: 'none',
                  border: 'none',
                  color: '#ff6b6b',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'background 0.2s',
                  display: 'block'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 107, 107, 0.2)';
                  console.log(t('storyTree.mouseEnterDeleteButton'));
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'none';
                  console.log(t('storyTree.mouseLeaveDeleteButton'));
                }}
                onClick={(e) => {
                  console.log(t('storyTree.deleteButtonClicked'));
                  e.preventDefault();
                  e.stopPropagation();
                  if (contextMenu.nodeId) {
                    console.log(t('storyTree.deleteNodeClicked'), contextMenu.nodeId);
                    handleDeleteNode(contextMenu.nodeId);
                  }
                }}
              >
                🗑️ {t('storyTree.deleteNode')}
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default StoryTree;